import PropTypes from 'prop-types'
import React from 'react'
import range from 'lodash-es/range'
import Helmet from 'react-helmet'

import SearchLink from '../../containers/searchLinkContainer'
import ChevronLeftIcon from '../icons/chevronLeft'
import ChevronRightIcon from '../icons/chevronRight'

import styles from './pagination.module.css'

/**
 * Return array of pages by given params with gap elements if needed. For pagination purpose. Examples:
 *  getPagesRange(1, 20, 10) -> [1, 2, 3, 4, 5, 6, 7, 8, 9, '...', 20]
 *  getPagesRange(11, 20, 10) -> [1, '...', 7, 8, 9, 10, 11, 12, 13, 14, '...', 20]
 * @param {Number} currentPage
 * @param {Number} pagesCount
 * @param {Number} maxDisplayedPages
 * @returns {Array}
 */
const getPagesRange = (currentPage, pagesCount, maxDisplayedPages) => {
  if (pagesCount <= maxDisplayedPages) {
    return range(1, pagesCount + 1)
  }

  let pagesRange = [currentPage]
  let minPage = currentPage - 1
  let maxPage = currentPage + 1
  let direction = 1

  while (pagesRange.length < maxDisplayedPages) {
    if (direction < 0 && minPage > 0) {
      pagesRange.unshift(minPage)
      minPage--
    }

    if (direction > 0 && maxPage <= pagesCount) {
      pagesRange.push(maxPage)
      maxPage++
    }

    direction *= -1
  }

  if (pagesRange[0] !== 1) {
    pagesRange.splice(0, 1, 1, '...')
  }

  if (pagesRange[pagesRange.length - 1] !== pagesCount) {
    pagesRange.pop()
    pagesRange.push('...', pagesCount)
  }

  return pagesRange
}

const scrollToTop = () => {
  window.scrollTo(0, 0)
}

const PaginationItem = ({ value, locationParams, isCurrentPage }) => {
  let content

  if (value === '...') {
    content = <span className={styles.pageGap}>{value}</span>
  } else if (isCurrentPage) {
    content = <span className={styles.pageCurrent}>{value}</span>
  } else {
    content = (
      <SearchLink
        locationParams={locationParams}
        className={styles.page}
        onClick={scrollToTop}
      >
        {value}
      </SearchLink>
    )
  }
  return <li className={styles.paginationListElement}>{content}</li>
}

const Pagination = ({
  currentPage = 1,
  currentSite,
  totalItemsCount,
  pageSize,
  locationParams,
  makeSearchLink,
  maxDisplayedPages = 10,
  baseUrl,
  maxPagesCount
}) => {
  const calculatedPagesCount = Math.ceil(totalItemsCount / pageSize)
  const pagesCount =
    maxPagesCount && calculatedPagesCount > maxPagesCount
      ? maxPagesCount
      : calculatedPagesCount
  const previousPageLinkParams =
    currentPage > 1
      ? {
          ...locationParams,
          page: currentPage - 1
        }
      : null
  const nextPageLinkParams =
    currentPage < pagesCount
      ? { ...locationParams, page: currentPage + 1 }
      : null
  const shouldShowPagination = pagesCount > 1

  const link = []
  if (previousPageLinkParams) {
    link.push({
      rel: 'prev',
      href: baseUrl + makeSearchLink(previousPageLinkParams, currentSite)
    })
  }
  if (nextPageLinkParams) {
    link.push({
      rel: 'next',
      href: baseUrl + makeSearchLink(nextPageLinkParams, currentSite)
    })
  }

  return shouldShowPagination ? (
    <nav className={styles.pagination} role="navigation">
      <Helmet link={link} />
      <ul className={styles.paginationItems}>
        {previousPageLinkParams ? (
          <li className={styles.paginationListElement}>
            <SearchLink
              locationParams={previousPageLinkParams}
              className={styles.paginationPrevious}
              onClick={scrollToTop}
              data-test-selector="paginationPrevious"
            >
              <ChevronLeftIcon width="8" height="12" />
            </SearchLink>{' '}
          </li>
        ) : null}
        {getPagesRange(currentPage, pagesCount, maxDisplayedPages).map(
          (pageNumber, index) => {
            const pageLinkParams = { ...locationParams, page: pageNumber }
            return (
              <PaginationItem
                key={index}
                value={pageNumber}
                locationParams={pageLinkParams}
                isCurrentPage={currentPage === pageNumber}
              />
            )
          }
        )}
        {nextPageLinkParams ? (
          <li className={styles.paginationListElement}>
            <SearchLink
              locationParams={nextPageLinkParams}
              className={styles.paginationNext}
              onClick={scrollToTop}
              data-test-selector="paginationNext"
            >
              <ChevronRightIcon width="8" height="12" />
            </SearchLink>
          </li>
        ) : null}
      </ul>
    </nav>
  ) : null
}

Pagination.propTypes = {
  currentPage: PropTypes.number,
  currentSite: PropTypes.string,
  totalItemsCount: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  locationParams: PropTypes.object.isRequired,
  makeSearchLink: PropTypes.func.isRequired,
  maxDisplayedPages: PropTypes.number,
  maxPagesCount: PropTypes.number
}

export default Pagination
